import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Spinner, Alert, Badge, Pagination, Button, Form, Row, Col, Modal, InputGroup } from 'react-bootstrap';
import useAuthHeaders from '../../hooks/useAuthHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';
const PAGE_SIZE = 25;
const MX_TZ = 'America/Mexico_City';

const ESTADOS = {
  activa: { label: 'Activa', bg: 'success' },
  vencida: { label: 'Vencida', bg: 'warning', text: 'dark' },
  revocada: { label: 'Revocada', bg: 'danger' },
  sinActivar: { label: 'Sin activar', bg: 'secondary' },
  aunNoVigente: { label: 'Aún no vigente', bg: 'info', text: 'dark' },
};

const TIPOS_EVENTO = {
  Alta: 'Alta',
  Activacion: 'Activación',
  TomaDeControl: 'Toma de control',
  HeartbeatOtroPar: 'Heartbeat de otro par',
  Revocacion: 'Revocación',
  Edicion: 'Edición',
  RotacionKey: 'Rotación de key',
};

// Las fechas llegan en UTC; si el JSON no trae zona se asume UTC. Se muestran en hora de México,
// sin depender de la zona del navegador.
const toDate = (utcStr) => {
  if (!utcStr) return null;
  const iso = /(Z|[+-]\d{2}:\d{2})$/.test(utcStr) ? utcStr : `${utcStr}Z`;
  return new Date(iso);
};

const formatDateTime = (utcStr) => {
  const d = toDate(utcStr);
  if (!d) return '—';
  return d.toLocaleString('es-MX', {
    timeZone: MX_TZ,
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDate = (utcStr) => {
  const d = toDate(utcStr);
  if (!d) return '—';
  return d.toLocaleDateString('es-MX', { timeZone: MX_TZ, year: 'numeric', month: 'short', day: 'numeric' });
};

// Fecha yyyy-mm-dd (día natural de México) de un instante UTC, para los <input type="date">.
const utcToMxDate = (utcStr) => {
  const d = toDate(utcStr);
  return d ? d.toLocaleDateString('en-CA', { timeZone: MX_TZ }) : '';
};

const todayMx = () => new Date().toLocaleDateString('en-CA', { timeZone: MX_TZ });

const shiftDate = (dateStr, { years = 0, days = 0 }) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y + years, m - 1, d + days)).toISOString().slice(0, 10);
};

// México no tiene horario de verano desde 2022 (UTC-6 fijo). La vigencia abarca días completos de México:
// desde las 00:00 del primer día hasta las 23:59:59 del último.
const mxDateStartUtc = (dateStr) => `${dateStr}T06:00:00.000Z`;
const mxDateEndUtc = (dateStr) => `${shiftDate(dateStr, { days: 1 })}T05:59:59.000Z`;

const readError = async (res) => {
  const text = await res.text().catch(() => '');
  try {
    const data = JSON.parse(text);
    if (data?.message) return data.message;
    if (data?.title) return data.title;
  } catch {
    // No es JSON: se usa el texto tal cual.
  }
  return text || `Error ${res.status}`;
};

const EstadoBadge = ({ estado }) => {
  const e = ESTADOS[estado] || { label: estado || '—', bg: 'secondary' };
  return <Badge bg={e.bg} text={e.text}>{e.label}</Badge>;
};

const RfcsBadge = ({ licencia }) => {
  const rfcs = licencia.instanciaVigente?.rfcsRegistrados;
  if (rfcs == null) return <span className="text-muted">—</span>;
  return licencia.excedeTope
    ? <Badge bg="danger" title="Excede el tope de la licencia">{rfcs} / {licencia.maxRfcs}</Badge>
    : <span>{rfcs} / {licencia.maxRfcs}</span>;
};

// La license key solo existe en el estado del modal que la muestra: se borra al cerrarlo y nunca se persiste.
const KeyReveal = ({ licenseKey }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(licenseKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Alert variant="warning" className="mb-3">
      <div className="fw-semibold mb-2">License key</div>
      <InputGroup className="mb-2">
        <Form.Control readOnly value={licenseKey} className="font-monospace" onFocus={(e) => e.target.select()} />
        <Button variant={copied ? 'success' : 'outline-dark'} onClick={copy}>{copied ? 'Copiada' : 'Copiar'}</Button>
      </InputGroup>
      <div className="small">
        Cópiala ahora: <strong>no se podrá volver a consultar</strong>. Si se pierde, hay que rotarla.
      </div>
    </Alert>
  );
};

const NuevaLicenciaModal = ({ show, onHide, onCreated }) => {
  const { getAuthHeaders } = useAuthHeaders();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null); // { licencia, key }

  useEffect(() => {
    if (show) {
      const hoy = todayMx();
      setForm({ cliente: '', userEmail: '', maxRfcs: 10, desde: hoy, hasta: shiftDate(hoy, { years: 1 }) });
      setError(null);
      setCreated(null);
    }
  }, [show]);

  const handleClose = () => {
    setCreated(null);
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/admin/licencias`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cliente: form.cliente.trim(),
          userEmail: form.userEmail.trim() || null,
          maxRfcs: Number(form.maxRfcs),
          vigenteDesdeUtc: mxDateStartUtc(form.desde),
          vigenteHastaUtc: mxDateEndUtc(form.hasta),
        }),
      });
      if (!res.ok) {
        setError(await readError(res));
        return;
      }
      const data = await res.json();
      setCreated(data);
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const invalid = !form || !form.cliente.trim() || !(Number(form.maxRfcs) >= 0) || !form.desde || !form.hasta || form.hasta < form.desde;

  return (
    <Modal show={show} onHide={handleClose} backdrop={created ? 'static' : true}>
      <Modal.Header closeButton>
        <Modal.Title>{created ? 'Licencia emitida' : 'Nueva licencia'}</Modal.Title>
      </Modal.Header>
      {created ? (
        <>
          <Modal.Body>
            <p className="mb-3">
              Licencia de <strong>{created.licencia?.cliente}</strong> ({created.licencia?.keyPrefijo}…) emitida.
            </p>
            <KeyReveal licenseKey={created.key} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleClose}>Ya la copié, cerrar</Button>
          </Modal.Footer>
        </>
      ) : form && (
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Cliente</Form.Label>
              <Form.Control value={form.cliente} onChange={set('cliente')} maxLength={200} required autoFocus />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Correo de la cuenta SatGo <span className="text-muted">(opcional)</span></Form.Label>
              <Form.Control type="email" value={form.userEmail} onChange={set('userEmail')} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Máximo de RFCs</Form.Label>
              <Form.Control type="number" min={0} value={form.maxRfcs} onChange={set('maxRfcs')} required />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Vigente desde</Form.Label>
                  <Form.Control type="date" value={form.desde} onChange={set('desde')} required />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Vigente hasta</Form.Label>
                  <Form.Control type="date" value={form.hasta} min={form.desde} onChange={set('hasta')} required />
                </Form.Group>
              </Col>
            </Row>
            <Form.Text className="text-muted">Días completos en hora de México (hasta las 23:59 del último día).</Form.Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={saving || invalid}>
              {saving ? <Spinner size="sm" animation="border" /> : 'Emitir licencia'}
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
};

const DetalleLicenciaModal = ({ licenciaId, onHide, onChanged }) => {
  const { getAuthHeaders } = useAuthHeaders();
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [diasMetricas, setDiasMetricas] = useState(30);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [confirmar, setConfirmar] = useState(null); // 'rotar' | 'revocar'
  const [accionando, setAccionando] = useState(false);
  const [rotatedKey, setRotatedKey] = useState(null);

  const aplicarDetalle = (d) => {
    setDetalle(d);
    setForm({
      cliente: d.cliente || '',
      maxRfcs: d.maxRfcs,
      desde: utcToMxDate(d.vigenteDesdeUtc),
      hasta: utcToMxDate(d.vigenteHastaUtc),
    });
  };

  const fetchDetalle = useCallback(async (id, dias) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/admin/licencias/${id}?diasMetricas=${dias}`, { headers });
      if (!res.ok) throw new Error(await readError(res));
      aplicarDetalle(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (licenciaId) {
      setDetalle(null);
      setForm(null);
      setFeedback(null);
      setConfirmar(null);
      setRotatedKey(null);
      setDiasMetricas(30);
      fetchDetalle(licenciaId, 30);
    }
  }, [licenciaId, fetchDetalle]);

  const handleClose = () => {
    setRotatedKey(null);
    setConfirmar(null);
    onHide();
  };

  const cambiarDias = (dias) => {
    setDiasMetricas(dias);
    fetchDetalle(licenciaId, dias);
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    // Solo se mandan los campos que cambiaron (null = se conserva). La vigencia se edita por días de México: si no
    // se tocó, reenviarla la movería a 00:00/23:59:59 aunque la guardada tenga otra hora.
    const body = {};
    if (form.cliente.trim() !== detalle.cliente) body.cliente = form.cliente.trim();
    if (Number(form.maxRfcs) !== detalle.maxRfcs) body.maxRfcs = Number(form.maxRfcs);
    if (form.desde !== utcToMxDate(detalle.vigenteDesdeUtc)) body.vigenteDesdeUtc = mxDateStartUtc(form.desde);
    if (form.hasta !== utcToMxDate(detalle.vigenteHastaUtc)) body.vigenteHastaUtc = mxDateEndUtc(form.hasta);
    if (Object.keys(body).length === 0) {
      setFeedback({ ok: true, message: 'Sin cambios.' });
      return;
    }

    setSaving(true);
    setFeedback(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/admin/licencias/${licenciaId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        setFeedback({ ok: false, message: await readError(res) });
        return;
      }
      // El PUT devuelve el detalle sin métricas del periodo elegido: se vuelve a pedir para conservarlo.
      await fetchDetalle(licenciaId, diasMetricas);
      setFeedback({ ok: true, message: 'Cambios guardados. La instancia los recibe en su siguiente heartbeat.' });
      onChanged();
    } catch (err) {
      setFeedback({ ok: false, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleAccion = async () => {
    const accion = confirmar;
    setAccionando(true);
    setFeedback(null);
    try {
      const headers = await getAuthHeaders();
      const ruta = accion === 'rotar' ? 'rotar-key' : 'revocar';
      const res = await fetch(`${API_BASE}/admin/licencias/${licenciaId}/${ruta}`, { method: 'POST', headers });
      if (!res.ok) {
        setFeedback({ ok: false, message: await readError(res) });
        return;
      }
      const data = await res.json();
      // Al revocar, una key rotada que siga en pantalla ya no sirve para nada: se quita.
      setRotatedKey(accion === 'rotar' ? data.key : null);
      await fetchDetalle(licenciaId, diasMetricas);
      setFeedback({ ok: true, message: accion === 'rotar' ? 'Key rotada.' : 'Licencia revocada.' });
      onChanged();
    } catch (err) {
      setFeedback({ ok: false, message: err.message });
    } finally {
      setAccionando(false);
      setConfirmar(null);
    }
  };

  const revocada = detalle?.estado === 'revocada';
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const formInvalido = !form || !form.cliente.trim() || !(Number(form.maxRfcs) >= 0) || !form.desde || !form.hasta || form.hasta < form.desde;

  return (
    <Modal show={!!licenciaId} onHide={handleClose} size="xl" backdrop={rotatedKey ? 'static' : true}>
      <Modal.Header closeButton>
        <Modal.Title>
          {detalle ? <>{detalle.cliente} <EstadoBadge estado={detalle.estado} /></> : 'Licencia'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && !detalle && <div className="text-center py-4"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {rotatedKey && <KeyReveal licenseKey={rotatedKey} />}
        {feedback && <Alert variant={feedback.ok ? 'success' : 'danger'} dismissible onClose={() => setFeedback(null)}>{feedback.message}</Alert>}

        {detalle && form && (
          <>
            <Row className="small text-muted mb-3">
              <Col md={4}>Licencia: <span className="font-monospace">{detalle.licenciaId}</span></Col>
              <Col md={2}>Key: <span className="font-monospace">{detalle.keyPrefijo}…</span></Col>
              <Col md={3}>Cuenta: {detalle.userEmail || '—'}</Col>
              <Col md={3}>Creada: {formatDateTime(detalle.creadaUtc)}</Col>
              {detalle.revocadaUtc && <Col md={4}>Revocada: {formatDateTime(detalle.revocadaUtc)}</Col>}
              {detalle.degradadaHastaUtc && <Col md={4}>Degradada hasta: {formatDateTime(detalle.degradadaHastaUtc)}</Col>}
            </Row>

            <Card className="mb-3">
              <Card.Header className="fw-semibold">Datos de la licencia</Card.Header>
              <Card.Body>
                {revocada && <Alert variant="secondary" className="py-2">La licencia está revocada: ya no se puede editar.</Alert>}
                <Form onSubmit={handleGuardar}>
                  <fieldset disabled={revocada || saving}>
                    <Row className="g-3 align-items-end">
                      <Col md={4}>
                        <Form.Label>Cliente</Form.Label>
                        <Form.Control value={form.cliente} onChange={set('cliente')} maxLength={200} required />
                      </Col>
                      <Col md={2}>
                        <Form.Label>Máx. RFCs</Form.Label>
                        <Form.Control type="number" min={0} value={form.maxRfcs} onChange={set('maxRfcs')} required />
                      </Col>
                      <Col md={2}>
                        <Form.Label>Vigente desde</Form.Label>
                        <Form.Control type="date" value={form.desde} onChange={set('desde')} required />
                      </Col>
                      <Col md={2}>
                        <Form.Label>Vigente hasta</Form.Label>
                        <Form.Control type="date" value={form.hasta} min={form.desde} onChange={set('hasta')} required />
                      </Col>
                      <Col md={2}>
                        <Button type="submit" variant="primary" className="w-100" disabled={formInvalido}>
                          {saving ? <Spinner size="sm" animation="border" /> : 'Guardar'}
                        </Button>
                      </Col>
                    </Row>
                  </fieldset>
                </Form>
              </Card.Body>
            </Card>

            {!revocada && (
              <Card className="mb-3">
                <Card.Header className="fw-semibold">Acciones</Card.Header>
                <Card.Body>
                  {confirmar === 'rotar' && (
                    <Alert variant="warning">
                      <p className="mb-2">
                        Se emite una key nueva y la actual deja de servir de inmediato. <strong>La instancia deja de hacer
                        heartbeat</strong> hasta que se le configure la key nueva.
                      </p>
                      <Button variant="warning" size="sm" className="me-2" onClick={handleAccion} disabled={accionando}>
                        {accionando ? <Spinner size="sm" animation="border" /> : 'Sí, rotar la key'}
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={() => setConfirmar(null)} disabled={accionando}>Cancelar</Button>
                    </Alert>
                  )}
                  {confirmar === 'revocar' && (
                    <Alert variant="danger">
                      <p className="mb-2">
                        <strong>La revocación es definitiva</strong>: no se puede reactivar. La instancia queda degradada
                        7 días.
                      </p>
                      <Button variant="danger" size="sm" className="me-2" onClick={handleAccion} disabled={accionando}>
                        {accionando ? <Spinner size="sm" animation="border" /> : 'Sí, revocar definitivamente'}
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={() => setConfirmar(null)} disabled={accionando}>Cancelar</Button>
                    </Alert>
                  )}
                  {!confirmar && (
                    <>
                      <Button variant="outline-warning" className="me-2" onClick={() => { setRotatedKey(null); setConfirmar('rotar'); }}>Rotar key</Button>
                      <Button variant="outline-danger" onClick={() => setConfirmar('revocar')}>Revocar</Button>
                    </>
                  )}
                </Card.Body>
              </Card>
            )}

            <h6 className="mt-4">Instancias</h6>
            {detalle.instancias?.length ? (
              <Table size="sm" responsive hover className="small">
                <thead>
                  <tr>
                    <th>Instance ID</th><th>Versión</th><th>RFCs</th><th>Primer contacto</th><th>Último contacto</th><th>Última IP</th>
                  </tr>
                </thead>
                <tbody>
                  {detalle.instancias.map((i) => (
                    <tr key={i.instanceId}>
                      <td className="font-monospace">
                        {i.instanceId} {i.esVigente && <Badge bg="success" className="ms-1">Vigente</Badge>}
                      </td>
                      <td>{i.version || '—'}</td>
                      <td>{i.rfcsRegistrados ?? '—'}</td>
                      <td>{formatDateTime(i.primerContactoUtc)}</td>
                      <td>{formatDateTime(i.ultimoContactoUtc)}</td>
                      <td className="font-monospace">{i.ultimaIp || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : <p className="text-muted small">Ninguna instancia ha activado esta licencia.</p>}

            <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
              <h6 className="mb-0">Métricas</h6>
              <Form.Select size="sm" style={{ width: 'auto' }} value={diasMetricas} onChange={(e) => cambiarDias(Number(e.target.value))} disabled={loading}>
                <option value={7}>Últimos 7 días</option>
                <option value={30}>Últimos 30 días</option>
                <option value={90}>Últimos 90 días</option>
              </Form.Select>
            </div>
            {detalle.metricas?.length ? (
              <Table size="sm" responsive hover className="small">
                <thead>
                  <tr>
                    <th>Instancia</th><th>Endpoint</th><th className="text-end">OK</th><th className="text-end">Falla de servicio</th>
                    <th className="text-end">Credenciales inválidas</th><th className="text-end">p95 máx.</th>
                  </tr>
                </thead>
                <tbody>
                  {detalle.metricas.map((m) => (
                    <tr key={`${m.instanceId}-${m.endpoint}`}>
                      <td className="font-monospace">{m.instanceId?.slice(0, 8)}…</td>
                      <td>{m.endpoint}</td>
                      <td className="text-end">{m.ok?.toLocaleString('es-MX')}</td>
                      <td className="text-end">{m.fallaServicio?.toLocaleString('es-MX')}</td>
                      <td className="text-end">{m.credencialesInvalidas?.toLocaleString('es-MX')}</td>
                      <td className="text-end">{m.p95MaxMs != null ? `${m.p95MaxMs.toLocaleString('es-MX')} ms` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : <p className="text-muted small">Sin métricas en el periodo.</p>}

            <h6 className="mt-4">Eventos <span className="text-muted small fw-normal">(últimos 50)</span></h6>
            {detalle.eventos?.length ? (
              <Table size="sm" responsive hover className="small">
                <thead>
                  <tr><th>Fecha</th><th>Tipo</th><th>Instancia</th><th>Versión</th><th>IP</th><th>Detalle</th></tr>
                </thead>
                <tbody>
                  {detalle.eventos.map((ev, idx) => (
                    <tr key={idx}>
                      <td className="text-nowrap">{formatDateTime(ev.ocurridoUtc)}</td>
                      <td>{TIPOS_EVENTO[ev.tipo] || ev.tipo}</td>
                      <td className="font-monospace">{ev.instanceId ? `${ev.instanceId.slice(0, 8)}…` : '—'}</td>
                      <td>{ev.version || '—'}</td>
                      <td className="font-monospace">{ev.ip || '—'}</td>
                      <td>{ev.detalle || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : <p className="text-muted small">Sin eventos.</p>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>{rotatedKey ? 'Ya la copié, cerrar' : 'Cerrar'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

const AdminLicencias = () => {
  const { getAuthHeaders } = useAuthHeaders();

  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [clienteInput, setClienteInput] = useState('');
  const [cliente, setCliente] = useState('');
  const [estado, setEstado] = useState('');

  const [showNueva, setShowNueva] = useState(false);
  const [detalleId, setDetalleId] = useState(null);

  const fetchLicencias = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (cliente) params.set('cliente', cliente);
      if (estado) params.set('estado', estado);
      const res = await fetch(`${API_BASE}/admin/licencias?${params}`, { headers });
      if (!res.ok) throw new Error(await readError(res));
      setResult(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, cliente, estado]);

  useEffect(() => { fetchLicencias(); }, [fetchLicencias]);

  const handleBuscar = (e) => {
    e.preventDefault();
    setPage(1);
    setCliente(clienteInput.trim());
  };

  const totalPages = Math.max(1, Math.ceil((result.totalCount || 0) / PAGE_SIZE));

  return (
    <div className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-0">Licencias on-premise</h4>
          <small className="text-muted">Emisión, vigencia, revocación y rotación de keys de la edición on-premise.</small>
        </div>
        <Button variant="primary" onClick={() => setShowNueva(true)}>Nueva licencia</Button>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <Form onSubmit={handleBuscar}>
            <Row className="g-2 align-items-end">
              <Col md={5}>
                <Form.Label className="small">Cliente</Form.Label>
                <Form.Control value={clienteInput} onChange={(e) => setClienteInput(e.target.value)} placeholder="Buscar por cliente" />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Estado</Form.Label>
                <Form.Select value={estado} onChange={(e) => { setPage(1); setEstado(e.target.value); }}>
                  <option value="">Todos</option>
                  {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button type="submit" variant="outline-primary" className="w-100">Buscar</Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : result.items?.length ? (
            <Table hover responsive className="mb-0 small align-middle">
              <thead>
                <tr>
                  <th>Cliente</th><th>Key</th><th>Estado</th><th>Vigencia</th><th>RFCs / máx.</th><th>Versión</th><th>Último contacto</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((l) => (
                  <tr key={l.licenciaId} style={{ cursor: 'pointer' }} onClick={() => setDetalleId(l.licenciaId)}>
                    <td>
                      <div className="fw-semibold">{l.cliente}</div>
                      {l.userEmail && <div className="text-muted">{l.userEmail}</div>}
                    </td>
                    <td className="font-monospace">{l.keyPrefijo}…</td>
                    <td><EstadoBadge estado={l.estado} /></td>
                    <td className="text-nowrap">{formatDate(l.vigenteDesdeUtc)} – {formatDate(l.vigenteHastaUtc)}</td>
                    <td><RfcsBadge licencia={l} /></td>
                    <td>{l.instanciaVigente?.version || '—'}</td>
                    <td className="text-nowrap">{formatDateTime(l.instanciaVigente?.ultimoContactoUtc)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted text-center py-5 mb-0">No hay licencias con esos filtros.</p>
          )}
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">{result.totalCount} licencias</small>
          <Pagination className="mb-0">
            <Pagination.Prev disabled={page <= 1} onClick={() => setPage((p) => p - 1)} />
            <Pagination.Item active>{page} / {totalPages}</Pagination.Item>
            <Pagination.Next disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} />
          </Pagination>
        </div>
      )}

      <NuevaLicenciaModal show={showNueva} onHide={() => setShowNueva(false)} onCreated={fetchLicencias} />
      <DetalleLicenciaModal licenciaId={detalleId} onHide={() => setDetalleId(null)} onChanged={fetchLicencias} />
    </div>
  );
};

export default AdminLicencias;
